import React, { useState } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Link,
  Box,
  Alert,
  CircularProgress,
  InputAdornment,
  Divider,
} from '@mui/material';
import { Email as EmailIcon, Lock as LockIcon, Error as ErrorIcon, Security as SecurityIcon } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { C } from '../theme';

interface User {
  id: number;
  email: string;
  full_name: string;
  userType: 'land_owner' | 'business_owner' | 'investor';
}

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [emailError, setEmailError] = useState(false);
  const [passwordError, setPasswordError] = useState(false);
  const [requiresCaptcha, setRequiresCaptcha] = useState(false);
  const [captchaQuestion, setCaptchaQuestion] = useState('');
  const [captchaAnswer, setCaptchaAnswer] = useState('');
  const [captchaError, setCaptchaError] = useState(false);
  const navigate = useNavigate();
  const { login } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setEmailError(false);
    setPasswordError(false);
    setCaptchaError(false);
    setLoading(true);

    // Basic validation
    if (!email.trim()) {
      setEmailError(true);
      setError('Email adresa je obavezna');
      setLoading(false);
      return;
    }

    if (!password.trim()) {
      setPasswordError(true);
      setError('Lozinka je obavezna');
      setLoading(false);
      return;
    }

    if (requiresCaptcha && !captchaAnswer.trim()) {
      setCaptchaError(true);
      setError('Molimo odgovorite na CAPTCHA pitanje');
      setLoading(false);
      return;
    }

    try {
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          password,
          captchaAnswer,
          captchaQuestion
        }),
      });

      const data = await response.json();
      console.log('Login response:', data);
      if (response.ok) {
        const userData: User = {
          id: data.user.id,
          email: data.user.email,
          full_name: data.user.full_name,
          userType: data.user.user_type ? 
            (data.user.user_type === 'land_owner' ? 'land_owner' : 
             data.user.user_type === 'business_owner' ? 'business_owner' : 'investor') : 
            (data.user.userType === 'land_owner' ? 'land_owner' : 
             data.user.userType === 'business_owner' ? 'business_owner' : 'investor')
        };
        login(data.token, userData);
        navigate('/');
      } else {
        if (data.requiresCaptcha) {
          setRequiresCaptcha(true);
          setCaptchaQuestion(data.captcha);
          setError(data.message);
        } else {
          setError(data.message || 'Greška pri prijavljivanju');
        }
      }
    } catch (err) {
      setError('Greška pri povezivanju sa serverom');
    } finally {
      setLoading(false);
    }
  };

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePassword = (password: string) => {
    return password.length >= 6;
  };

  const getEmailError = () => {
    if (!email) return '';
    if (!validateEmail(email)) return 'Molimo unesite validnu email adresu';
    return '';
  };

  const getPasswordError = () => {
    if (!password) return '';
    if (!validatePassword(password)) return 'Lozinka mora imati najmanje 6 karaktera';
    return '';
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
    if (emailError) {
      setEmailError(false);
      setError('');
    }
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value);
    if (passwordError) {
      setPasswordError(false);
      setError('');
    }
  };

  const handleCaptchaChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCaptchaAnswer(e.target.value);
    if (captchaError) {
      setCaptchaError(false);
      setError('');
    }
  };

  return (
    <Box
      sx={{
        pt: '110px',
        pb: 8,
        backgroundColor: C.bg,
        minHeight: '100vh',
        backgroundImage:
          'radial-gradient(ellipse 70% 50% at 50% -10%, rgba(243,192,21,0.10), transparent 70%)',
      }}
    >
      <Container maxWidth="sm">
        <Box sx={{ textAlign: 'center', mb: 5 }}>
          <Typography variant="h2" sx={{ fontWeight: 800, mb: 1.5, color: C.white, fontSize: { xs: '2rem', md: '2.6rem' } }}>
            Prijava
          </Typography>
          <Typography variant="h6" sx={{ color: C.muted, maxWidth: 420, mx: 'auto', lineHeight: 1.6, fontWeight: 400, fontSize: '1.05rem' }}>
            Prijavite se na vaš nalog i pristupite svim funkcionalnostima platforme
          </Typography>
        </Box>

        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, md: 5 },
            backgroundColor: C.surface,
            borderRadius: 4,
            border: `1px solid ${C.line}`,
          }}
        >
          {error && (
            <Alert 
              severity="error" 
              sx={{ 
                mb: 4,
                '& .MuiAlert-icon': {
                  color: '#dc3545',
                },
              }}
              icon={<ErrorIcon />}
            >
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label="Email adresa"
              type="email"
              value={email}
              onChange={handleEmailChange}
              margin="normal"
              required
              error={emailError}
              helperText={getEmailError()}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailIcon color={emailError ? 'error' : 'action'} />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiFormHelperText-root.Mui-error': {
                  color: C.danger,
                  fontWeight: 500,
                },
              }}
            />
            <TextField
              fullWidth
              label="Lozinka"
              type="password"
              value={password}
              onChange={handlePasswordChange}
              margin="normal"
              required
              error={passwordError}
              helperText={getPasswordError()}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockIcon color={passwordError ? 'error' : 'action'} />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiFormHelperText-root.Mui-error': {
                  color: C.danger,
                  fontWeight: 500,
                },
              }}
            />
            
            {/* CAPTCHA Section - Only show when required */}
            {requiresCaptcha && (
              <>
                <Divider sx={{ my: 3, borderColor: C.line }} />

                <Box sx={{
                  p: 3,
                  backgroundColor: C.surface2,
                  borderRadius: 2,
                  border: `1px solid ${C.line}`,
                  mb: 2
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <SecurityIcon sx={{ color: C.gold, mr: 1 }} />
                    <Typography variant="h6" sx={{ color: C.white, fontWeight: 600 }}>
                      Dokazite da niste robot
                    </Typography>
                  </Box>

                  <Typography variant="body1" sx={{ mb: 2, color: C.muted }}>
                    Previše neuspešnih pokušaja prijave. Molimo odgovorite na sledeće pitanje:
                  </Typography>

                  <Typography variant="h6" sx={{
                    mb: 2,
                    color: C.white,
                    fontWeight: 700,
                    backgroundColor: C.bg,
                    p: 2,
                    borderRadius: 1,
                    border: `1px solid ${C.line}`
                  }}>
                    {captchaQuestion}
                  </Typography>
                </Box>
                
                <TextField
                  fullWidth
                  label="Vaš odgovor"
                  type="text"
                  value={captchaAnswer}
                  onChange={handleCaptchaChange}
                  margin="normal"
                  required
                  error={captchaError}
                  helperText={captchaError ? 'Molimo odgovorite na CAPTCHA pitanje' : ''}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SecurityIcon color={captchaError ? 'error' : 'action'} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiFormHelperText-root.Mui-error': {
                      color: C.danger,
                      fontWeight: 500,
                    },
                  }}
                />
              </>
            )}
            
            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={loading}
              sx={{ mt: 3, mb: 2, py: 1.5, fontSize: '1.05rem' }}
            >
              {loading ? <CircularProgress size={24} sx={{ color: C.onGold }} /> : 'Prijavi se'}
            </Button>
          </Box>

          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Link
              component={RouterLink}
              to="/forgot-password"
              sx={{ color: C.muted, textDecoration: 'none', fontSize: '14px', '&:hover': { color: C.gold, textDecoration: 'underline' } }}
            >
              Zaboravili ste lozinku?
            </Link>
          </Box>

          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="body2" sx={{ color: C.muted }}>
              Nemate nalog?{' '}
              <Link
                component={RouterLink}
                to="/register"
                sx={{ color: '#F6CB4B', textDecoration: 'none', fontWeight: 700, '&:hover': { textDecoration: 'underline' } }}
              >
                Registrujte se ovde
              </Link>
            </Typography>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default Login; 